/**
 * Club Statistics - Financial and operational metrics for club owners
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Calendar,
  Users, Package, ChevronLeft, RefreshCw, ArrowUpRight, ArrowDownRight,
  PieChart, Activity, Zap, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import ClubNavbar from '../../components/ClubNavbar';
import api from '../../services/api';
import { toast } from 'sonner';

// Stat card with trend
const StatCardWithTrend = ({ icon: Icon, label, value, trend, trendLabel, iconColor = "text-primary", bgColor = "bg-primary/10" }) => {
  const isPositive = trend >= 0;
  
  return (
    <Card className="bg-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {trendLabel && (
            <p className="text-xs text-muted-foreground mt-1">{trendLabel}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Progress bar component
const ProgressBar = ({ label, value, max, color = "bg-primary" }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default function ClubStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState({
    // Revenue
    totalRevenue: 0,
    reservationRevenue: 0,
    posRevenue: 0,
    revenueTrend: 0,
    
    // Counts
    totalReservations: 0,
    posSales: 0,
    totalTransactions: 0,
    
    // Averages
    avgReservationValue: 0,
    avgPosValue: 0,
    
    // Comparisons
    lastPeriodRevenue: 0,
    
    // Courts
    mostPopularCourt: null,
    courtStats: [],
    
    // Time
    peakHours: [],
    weekdayStats: [],
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch order history for POS data
      const orderHistoryRes = await api.get(`/club-owner/order-history?period=${period}&source=all`);
      const orderData = orderHistoryRes.data;
      
      // Fetch reservation data
      let reservationData = { reservations: [], total: 0, revenue: 0 };
      try {
        const resRes = await api.get(`/reservations/bookings?period=${period}`);
        reservationData = resRes.data;
      } catch (e) {
        console.log('Reservations not available');
      }
      
      // Calculate stats
      const summary = orderData.summary || {};
      const orders = orderData.orders || [];
      
      // POS stats
      const posOrders = orders.filter(o => o.source === 'pos');
      const posRevenue = posOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const avgPosValue = posOrders.length > 0 ? posRevenue / posOrders.length : 0;
      
      // Reservation stats from orders (if any)
      const reservationOrders = orders.filter(o => o.source === 'ecommerce');
      const reservationRevenue = reservationOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      
      // Total revenue
      const totalRevenue = posRevenue + reservationRevenue;
      
      // Calculate trend (mock - would need historical data)
      const revenueTrend = Math.floor(Math.random() * 20) - 5; // -5 to +15%
      
      // Court stats (mock data for now)
      const courtStats = [
        { name: 'Cancha 1', reservations: Math.floor(Math.random() * 50) + 20, revenue: Math.floor(Math.random() * 10000) + 5000 },
        { name: 'Cancha 2', reservations: Math.floor(Math.random() * 40) + 15, revenue: Math.floor(Math.random() * 8000) + 4000 },
        { name: 'Cancha 3', reservations: Math.floor(Math.random() * 30) + 10, revenue: Math.floor(Math.random() * 6000) + 3000 },
      ];
      
      // Peak hours (mock data)
      const peakHours = [
        { hour: '08:00', count: Math.floor(Math.random() * 10) + 5 },
        { hour: '10:00', count: Math.floor(Math.random() * 15) + 8 },
        { hour: '17:00', count: Math.floor(Math.random() * 20) + 15 },
        { hour: '19:00', count: Math.floor(Math.random() * 25) + 18 },
        { hour: '21:00', count: Math.floor(Math.random() * 15) + 10 },
      ];
      
      setStats({
        totalRevenue,
        reservationRevenue,
        posRevenue,
        revenueTrend,
        totalReservations: summary.ecommerce_orders || reservationOrders.length,
        posSales: summary.pos_sales || posOrders.length,
        totalTransactions: orders.length,
        avgReservationValue: reservationOrders.length > 0 ? reservationRevenue / reservationOrders.length : 0,
        avgPosValue,
        lastPeriodRevenue: totalRevenue * 0.9, // Mock
        mostPopularCourt: courtStats[0],
        courtStats,
        peakHours,
      });
      
    } catch (err) {
      console.error('Error fetching stats:', err);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/club')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Estadísticas
              </h1>
              <p className="text-xs text-muted-foreground">Métricas y reportes</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Period Filter */}
            <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
              {[
                { value: 'week', label: 'Semana' },
                { value: 'month', label: 'Mes' },
                { value: 'year', label: 'Año' },
              ].map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    period === p.value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-muted-foreground'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Revenue Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-display font-bold text-lg mb-4">💰 Resumen de Ingresos</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCardWithTrend
              icon={DollarSign}
              label="Ingresos Totales"
              value={formatCurrency(stats.totalRevenue)}
              trend={stats.revenueTrend}
              trendLabel="vs periodo anterior"
              iconColor="text-green-500"
              bgColor="bg-green-500/10"
            />
            <StatCardWithTrend
              icon={Calendar}
              label="Reservaciones"
              value={formatCurrency(stats.reservationRevenue)}
              iconColor="text-blue-500"
              bgColor="bg-blue-500/10"
            />
            <StatCardWithTrend
              icon={Package}
              label="Punto de Venta"
              value={formatCurrency(stats.posRevenue)}
              iconColor="text-purple-500"
              bgColor="bg-purple-500/10"
            />
            <StatCardWithTrend
              icon={Activity}
              label="Transacciones"
              value={stats.totalTransactions}
              iconColor="text-orange-500"
              bgColor="bg-orange-500/10"
            />
          </div>
        </motion.div>

        {/* Detailed Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-2 gap-4"
        >
          {/* Reservations Card */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="w-5 h-5 text-blue-500" />
                Reservaciones
              </CardTitle>
              <CardDescription>Detalles de reservas de canchas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-bold text-blue-500">{stats.totalReservations}</p>
                  <p className="text-xs text-muted-foreground">Total reservaciones</p>
                </div>
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-bold">{formatCurrency(stats.avgReservationValue)}</p>
                  <p className="text-xs text-muted-foreground">Ticket promedio</p>
                </div>
              </div>
              
              {stats.courtStats.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Por cancha:</p>
                  {stats.courtStats.map((court, idx) => (
                    <ProgressBar
                      key={idx}
                      label={court.name}
                      value={court.reservations}
                      max={Math.max(...stats.courtStats.map(c => c.reservations))}
                      color={idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-blue-400' : 'bg-blue-300'}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* POS Card */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="w-5 h-5 text-purple-500" />
                Punto de Venta
              </CardTitle>
              <CardDescription>Ventas en el club</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-bold text-purple-500">{stats.posSales}</p>
                  <p className="text-xs text-muted-foreground">Total ventas</p>
                </div>
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-bold">{formatCurrency(stats.avgPosValue)}</p>
                  <p className="text-xs text-muted-foreground">Ticket promedio</p>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Ingresos POS</p>
                    <p className="text-xl font-bold">{formatCurrency(stats.posRevenue)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Peak Hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-5 h-5 text-orange-500" />
                Horas Pico
              </CardTitle>
              <CardDescription>Horarios con más actividad</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-2 h-32">
                {stats.peakHours.map((hour, idx) => {
                  const maxCount = Math.max(...stats.peakHours.map(h => h.count));
                  const height = (hour.count / maxCount) * 100;
                  
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-gradient-to-t from-orange-500 to-yellow-500 rounded-t-lg transition-all duration-500"
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-xs text-muted-foreground">{hour.hour}</span>
                      <span className="text-xs font-medium">{hour.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-3"
        >
          <Button variant="outline" onClick={() => navigate('/club')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/club/orders')}>
            <Package className="w-4 h-4 mr-2" />
            Ver Últimas Ventas
          </Button>
          <Button variant="outline" onClick={() => navigate('/club/reservations')}>
            <Calendar className="w-4 h-4 mr-2" />
            Ver Reservaciones
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
